import type { Character, Scene, UploadedAssetManifest, UploadedAssetRecord } from "../types";

/**
 * Asset matching: turns a flat upload manifest into the Simple-mode model of
 * "people and places, each with looks" and reconciles it against the parsed script.
 *
 * The user's own naming convention drives everything:
 *   "Elle Look 05 - Warehouse District Look.png"  → person Elle, look 5, "Warehouse District"
 *   "Hansen Int Look 03 - Hansen Workstation.png" → place Hansen Int, look 3, "Hansen Workstation"
 *   "Dana Master Character Turnaround.png"        → person Dana, master reference
 *
 * Matching never mutates the engine's source-of-truth rules: uploads stay uploads,
 * we only *attach* them to characters/scenes so nothing shows as "missing".
 */

export interface ParsedLookName {
  /** Subject as written before the look marker, e.g. "Elle", "Warehouse District". */
  subject: string;
  /** 1-based look number when present. */
  lookNumber?: number;
  /** Human label after the dash, e.g. "Warehouse District", "Trailer Entrance". */
  lookLabel: string;
  /** Master/turnaround reference sheets get flagged; they're the default identity card. */
  isMaster: boolean;
}

export interface AssetLook {
  asset: UploadedAssetRecord;
  parsed: ParsedLookName;
}

export interface MatchedPerson {
  /** Canonical character name from the script when matched, else the upload's subject. */
  name: string;
  characterId?: string;
  /** True when this person was found in the parsed script's cast. */
  inScript: boolean;
  looks: AssetLook[];
  /** The master turnaround when present, else the first look — the identity reference. */
  defaultLook?: AssetLook;
}

export interface MatchedPlace {
  /** Subject grouping from uploads, e.g. "Warehouse District". */
  name: string;
  looks: AssetLook[];
  defaultLook?: AssetLook;
  /** Scene ids whose location/slugline matched this place. */
  sceneIds: string[];
}

export interface SceneLookPick {
  sceneId: string;
  characterName: string;
  look: AssetLook;
  /** Why this look was picked, e.g. "matched scene location". Shown as "Kinetix picked …". */
  reason: "location_match" | "label_match" | "master" | "first";
}

const LOOK_RE = /^(.*?)\s+look\s*(\d+)\s*[-–—:]?\s*(.*)$/i;
const MASTER_RE = /^(.*?)\s+master\b/i;

/**
 * Browser-displayable URL for an uploaded/generated asset. "local://" source URLs are storage-key
 * URIs (not fetchable); those resolve through the API's asset-object route, which both the local
 * server and the Cloudflare Worker serve.
 */
export function assetDisplayUrl(record: Pick<UploadedAssetRecord, "sourceUrl" | "storageKey">, width?: number): string {
  const source = record.sourceUrl || "";
  if (source && !source.startsWith("local://")) return source;
  const key = record.storageKey || (source ? source.replace("local://", "") : "");
  if (!key) return "";
  const base = `/api/assets/object/${key.split("/").map(encodeURIComponent).join("/")}`;
  // Thumbnails: the local server downscales on demand (?w=...); other backends ignore the param.
  return width && width > 0 ? `${base}?w=${Math.min(1024, Math.round(width))}` : base;
}

/** Displayable URL for a storyboard frame (generated frames persist with local:// storage keys). */
export function frameDisplayUrl(frame: { imageUrl?: string; sourceUrl?: string; storageKey?: string | null }, width?: number): string {
  const direct = frame.imageUrl && !frame.imageUrl.startsWith("local://") ? frame.imageUrl : "";
  if (direct) return direct;
  return assetDisplayUrl({ sourceUrl: frame.sourceUrl || frame.imageUrl || "", storageKey: frame.storageKey ?? undefined }, width);
}

function clean(value: string): string {
  return value.replace(/\.[a-z0-9]+$/i, "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
}

function normalize(value: string | undefined): string {
  return (value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
}

/** Tokens worth matching on — drops connective noise so "The office" matches "Office Interior". */
function tokens(value: string | undefined): string[] {
  const stop = new Set(["the", "a", "an", "of", "look", "looks", "int", "ext", "interior", "exterior", "day", "night", "shot", "scene"]);
  return normalize(value).split(" ").filter((token) => token.length > 1 && !stop.has(token));
}

function tokenOverlap(left: string | undefined, right: string | undefined): number {
  const a = new Set(tokens(left));
  const b = tokens(right);
  if (!a.size || !b.length) return 0;
  let hits = 0;
  for (const token of b) if (a.has(token)) hits += 1;
  return hits / Math.max(a.size, b.length);
}

/** Parse an upload's label/filename into subject + look metadata. Returns null when it doesn't follow the convention. */
export function parseLookName(record: UploadedAssetRecord): ParsedLookName | null {
  const raw = clean(record.label || record.fileName || "");
  if (!raw) return null;
  const master = raw.match(MASTER_RE);
  if (master) {
    return { subject: master[1].trim(), lookLabel: "Master reference", isMaster: true };
  }
  const look = raw.match(LOOK_RE);
  if (look) {
    const label = clean(look[3] || "");
    return {
      subject: look[1].trim(),
      lookNumber: Number(look[2]),
      lookLabel: label || `Look ${Number(look[2])}`,
      isMaster: false
    };
  }
  // No convention marker: the whole name is the subject (single-look upload like "Dana.png").
  return { subject: raw, lookLabel: raw, isMaster: false };
}

function groupBySubject(records: UploadedAssetRecord[]): Map<string, AssetLook[]> {
  const groups = new Map<string, AssetLook[]>();
  // Re-uploads of the same file stack as duplicate records; the newest one wins.
  const byFileName = new Map<string, UploadedAssetRecord>();
  for (const record of records) {
    byFileName.set(`${record.characterName || ""}::${record.fileName}`, record);
  }
  for (const asset of byFileName.values()) {
    if (asset.disabled) continue;
    if (!asset.storageKey && !asset.sourceUrl) continue;
    const parsed = parseLookName(asset);
    if (!parsed) continue;
    const key = normalize(asset.characterName || parsed.subject) || normalize(parsed.subject);
    if (!key) continue;
    const list = groups.get(key) || [];
    list.push({ asset, parsed });
    groups.set(key, list);
  }
  for (const list of groups.values()) {
    list.sort((a, b) => (a.parsed.lookNumber ?? 99) - (b.parsed.lookNumber ?? 99));
  }
  return groups;
}

function defaultLookFor(looks: AssetLook[]): AssetLook | undefined {
  return looks.find((look) => look.parsed.isMaster) || looks[0];
}

/** Group character-card uploads into people and reconcile against the parsed cast. */
export function matchPeople(characters: Character[], manifest?: UploadedAssetManifest): MatchedPerson[] {
  const groups = groupBySubject(manifest?.characterCards || []);
  const people: MatchedPerson[] = [];
  const claimed = new Set<string>();

  for (const character of characters) {
    const key = normalize(character.name);
    // Exact subject match first, then "Young Elle" ⊇ "Elle"-style containment.
    let looks = groups.get(key);
    let matchedKey = key;
    if (!looks) {
      for (const [groupKey, groupLooks] of groups) {
        if (claimed.has(groupKey)) continue;
        if (groupKey === key || tokenOverlap(groupKey, key) >= 0.99) {
          looks = groupLooks;
          matchedKey = groupKey;
          break;
        }
      }
    }
    if (looks) claimed.add(matchedKey);
    people.push({
      name: character.name,
      characterId: character.id,
      inScript: true,
      looks: looks || [],
      defaultLook: looks ? defaultLookFor(looks) : undefined
    });
  }

  // Uploads that didn't match anyone in the script still surface (e.g. "Young Elle" before analysis).
  for (const [key, looks] of groups) {
    if (claimed.has(key)) continue;
    people.push({
      name: looks[0].parsed.subject || looks[0].asset.characterName || key,
      inScript: false,
      looks,
      defaultLook: defaultLookFor(looks)
    });
  }
  return people;
}

/** Group scene-reference uploads into places and attach the scenes whose location matches. */
export function matchPlaces(scenes: Scene[], manifest?: UploadedAssetManifest): MatchedPlace[] {
  const groups = groupBySubject(manifest?.sceneReferences || []);
  const places: MatchedPlace[] = [];
  for (const [, looks] of groups) {
    const name = looks[0].parsed.subject || looks[0].asset.label || "Place";
    const sceneIds = scenes
      .filter((scene) => {
        const sceneText = `${scene.location} ${scene.slugline}`;
        return tokenOverlap(name, sceneText) >= 0.5 || tokenOverlap(sceneText, name) >= 0.5;
      })
      .map((scene) => scene.id);
    places.push({ name, looks, defaultLook: defaultLookFor(looks), sceneIds });
  }
  return places;
}

/**
 * Auto-pick the right look for a character in a scene: a look whose label matches the
 * scene's location wins ("Warehouse District Look" in a warehouse scene), then any label
 * overlap with the synopsis, then the master sheet, then look 1.
 */
export function pickLookForScene(
  person: { name: string; looks: AssetLook[]; defaultLook?: AssetLook },
  scene: Scene
): SceneLookPick | null {
  if (!person.looks.length) return null;
  const sceneText = `${scene.location} ${scene.slugline}`;
  let best: { look: AssetLook; score: number; reason: SceneLookPick["reason"] } | null = null;
  for (const look of person.looks) {
    if (look.parsed.isMaster) continue;
    const locationScore = tokenOverlap(look.parsed.lookLabel, sceneText);
    const synopsisScore = tokenOverlap(look.parsed.lookLabel, scene.synopsis) * 0.6;
    const score = Math.max(locationScore, synopsisScore);
    if (score > (best?.score ?? 0)) {
      best = { look, score, reason: locationScore >= synopsisScore ? "location_match" : "label_match" };
    }
  }
  if (best && best.score >= 0.34) {
    return { sceneId: scene.id, characterName: person.name, look: best.look, reason: best.reason };
  }
  const fallback = person.defaultLook || person.looks[0];
  return {
    sceneId: scene.id,
    characterName: person.name,
    look: fallback,
    reason: fallback.parsed.isMaster ? "master" : "first"
  };
}

/**
 * Reconcile parsed characters against uploads: anyone with a matched card upload becomes
 * cardStatus "uploaded" with the upload's label. This is what turns "10 characters need to be
 * resolved" into "your photo of Dana is already in". Locked characters are never touched.
 */
export function reconcileCharactersWithUploads(characters: Character[], manifest?: UploadedAssetManifest): Character[] {
  const people = matchPeople(characters, manifest);
  const byId = new Map(people.filter((p) => p.characterId).map((p) => [p.characterId as string, p]));
  return characters.map((character) => {
    if (character.cardLocked) return character;
    const person = byId.get(character.id);
    if (!person?.defaultLook) return character;
    if (character.cardStatus === "uploaded") return character;
    return {
      ...character,
      cardStatus: "uploaded",
      cardSource: "user_upload",
      cardAssetLabel: person.defaultLook.asset.label || person.defaultLook.asset.fileName
    };
  });
}
